import React, { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import {
  RELEASE_THEMES,
  themeColours,
  themeType,
  type ReleaseKitLook,
  type ThemeTextureKind,
} from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../../common/types';
import { kitFontName } from '../../../themes/kitWebFonts';
import { releaseThemeMessages } from '../../../themes/releaseThemeMessages';
import { imageTileMessages } from './imageTileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the image tiles; each part uses its own slice of it */
import styles from './ImageTiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TEXTURES: Record<ThemeTextureKind, MessageDescriptor> = {
  none: imageTileMessages.textureNone,
  grain: imageTileMessages.textureGrain,
  glow: imageTileMessages.textureGlow,
  paper: imageTileMessages.texturePaper,
};

interface FromYourThemeProps {
  look: ReleaseKitLook;
  palette: readonly string[];
  /** Opens the theme of the song (the Studio theme dialog); absent: no link. */
  onChangeTheme?: () => void;
}

/**
 * The read-only "From your theme" line of an image (design studio-details a1): the theme decides
 * the colours, the fonts and the texture, so step 3 shows them and links to "Change theme". It
 * replaces the Instructions of an image (owner decision 2026-10-08).
 */
const FromYourThemeComponent: React.FC<FromYourThemeProps> = ({ look, palette, onChangeTheme }) => {
  const intl = useIntl();
  const colours = useMemo(() => themeColours(look.themeId, palette), [look.themeId, palette]);
  const fonts = useMemo(() => {
    const type = themeType(look);
    const display = kitFontName(type.fonts.display);
    const text = kitFontName(type.fonts.text);
    return display === text ? display : `${display} + ${text}`;
  }, [look]);
  const texture = intl.formatMessage(TEXTURES[RELEASE_THEMES[look.themeId].texture.kind]);
  const swatches = [
    { role: 'background', colour: colours.background },
    { role: 'surface', colour: colours.surface },
    { role: 'text', colour: colours.text },
    { role: 'accent', colour: colours.accent },
  ];

  return (
    <div className={styles.fromTheme} data-testid="from-your-theme">
      <span className={styles.fromThemeLabel}>{intl.formatMessage(imageTileMessages.fromTheme)}</span>
      <b className={styles.fromThemeName}>{intl.formatMessage(releaseThemeMessages[look.themeId].name)}</b>
      <span className={styles.fromThemeItem}>
        <span className={styles.fromThemeLabel}>{intl.formatMessage(imageTileMessages.fromThemeColours)}</span>
        <span className={styles.swatches}>
          {swatches.map(({ role, colour }) => (
            <i key={role} style={{ background: colour }} />
          ))}
        </span>
      </span>
      <span className={styles.fromThemeItem}>
        <span className={styles.fromThemeLabel}>{intl.formatMessage(imageTileMessages.fromThemeFonts)}</span>
        {fonts}
      </span>
      <span className={styles.fromThemeItem}>
        <span className={styles.fromThemeLabel}>{intl.formatMessage(imageTileMessages.fromThemeTexture)}</span>
        {texture}
      </span>
      <span className={styles.grow} />
      {onChangeTheme !== undefined && (
        <button type="button" className="btn btn-link btn-sm p-0" onClick={onChangeTheme}>
          {intl.formatMessage(imageTileMessages.changeTheme)}
        </button>
      )}
    </div>
  );
};

export const FromYourTheme = TypedMemo(FromYourThemeComponent);
